import { httpClient, TokenManager, API_BASE_URL, LONG_RUNNING_TIMEOUT } from './httpClient';
import type { ToolWidgetData } from '../types/agentWidgets';

export interface ReportSection {
  id: string;
  title: string;
  placeholder?: string;
  required: boolean;
}

export interface Report {
  id: string;
  caseId: string;
  templateId?: string;
  userId: string;
  title: string;
  status: 'generating' | 'complete' | 'archived' | 'failed';
  content: string; // ProseMirror JSON as string
  generationPrompt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReportGenerationProgress {
  reportId: string;
  stage: string;       // PREPARING, GENERATING, PROCESSING, COMPLETED, FAILED
  progress: number;    // 0.0–1.0
  complete: boolean;
  failed: boolean;
  error: string | null;
  startedAtMillis: number;
}

export interface GenerateReportRequest {
  caseId: string;
  templateId: string;
  title: string;
  userPrompt?: string;
  selectedObjectiveIds?: string[];
  selectedDocumentIds?: string[];
  selectedQuestions?: string[];
  selectedKeyFindingIds?: string[];
  focusOnKeyFindings?: boolean;
}

export interface UpdateReportRequest {
  title?: string;
  status?: 'generating' | 'complete' | 'archived' | 'failed';
  content?: string;
}

export interface ObjectiveSummary {
  id: string;
  type: string;
  status: string;
  name: string;
}

export interface DocumentSummary {
  id: string;
  name: string;
  type: string;
}

export interface CaseDataSummary {
  caseId: string;
  caseName: string;
  caseDescription: string;
  patientName: string;
  objectives: ObjectiveSummary[];
  documents: DocumentSummary[];
  instructionLetter?: InstructionLetterSummary;
}

export interface InstructionLetterSummary {
  caseSummary?: string;
  admittedNegligence?: string[];
  disputedIssues?: string[];
  questionsToAnswer?: Array<{
    number: number;
    summary: string;
    fullText: string;
  }>;
  reportType?: string;
  parties?: {
    claimant?: string;
    defendant?: string;
    solicitor?: string;
    expert?: string;
  };
}

export interface RevisionContextFields {
  caseId?: string;
  documentIds?: string[];
  guidelineIds?: string[];
  objectiveIds?: string[];
}

export interface ReviseReportRequest extends RevisionContextFields {
  instruction: string;
}

export interface ReviseSelectionRequest extends RevisionContextFields {
  selectedText: string;
  instruction: string;
  beforeContext?: string;
  afterContext?: string;
}

export interface RevisionResponse {
  revisedContent: string;
}

export interface GenerateContentRequest {
  caseId: string;
  instruction: string;
  insertionContext?: string;
  documentIds?: string[];
  guidelineIds?: string[];
  objectiveIds?: string[];
}

export interface AskRequest {
  instruction: string;
  caseId?: string;
  documentIds?: string[];
  guidelineIds?: string[];
  objectiveIds?: string[];
  imageContext?: string;
}

export interface ContextLoadedEvent {
  documentsLoaded: number;
  documentsRequested: number;
  guidelinesLoaded: number;
  guidelinesRequested: number;
  objectivesLoaded: number;
  objectivesRequested: number;
  totalLoaded: number;
  totalRequested: number;
  failures: Array<{ type: string; id: string; reason: string }>;
  contextChars: number;
}

/**
 * Parse an SSE stream from a POST response, calling onToken for each token
 * and onDone when the stream completes.
 */
async function consumeSseStream(
  response: Response,
  onToken: (token: string) => void,
  onDone: () => void,
  onError: (error: Error) => void,
  onContextLoaded?: (metadata: ContextLoadedEvent) => void,
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    onError(new Error('No response body'));
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';

  // These must persist across chunk reads — a single SSE event can span multiple chunks.
  let eventName = '';
  let dataLines: string[] = [];

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer
      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5));
        } else if (line === '') {
          // Event boundary — dispatch accumulated data
          if (dataLines.length > 0) {
            const data = dataLines.join('\n');
            if (eventName === 'token') {
              onToken(data);
            } else if (eventName === 'context-loaded' && onContextLoaded) {
              try { onContextLoaded(JSON.parse(data)); } catch { /* ignore parse errors */ }
            } else if (eventName === 'done') {
              onDone();
              return;
            } else if (eventName === 'error') {
              onError(new Error(data || 'Streaming failed'));
              return;
            }
            dataLines = [];
          }
          eventName = '';
        }
      }
    }
    // Stream ended without a done event
    onDone();
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)));
  }
}

/**
 * Make a POST request that returns an SSE stream.
 */
async function postSseStream(
  endpoint: string,
  body: object,
  onToken: (token: string) => void,
  onDone: () => void,
  onError: (error: Error) => void,
  onContextLoaded?: (metadata: ContextLoadedEvent) => void,
): Promise<void> {
  const token = TokenManager.getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      credentials: 'include',
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      onError(new Error(`Streaming request failed (${response.status}): ${errorText}`));
      return;
    }

    await consumeSseStream(response, onToken, onDone, onError, onContextLoaded);
  } catch (err) {
    onError(err instanceof Error ? err : new Error(String(err)));
  }
}

export const reportApi = {
  // Create new report (from objective or blank)
  createReport: async (request: Partial<Report>): Promise<Report> => {
    return await httpClient.post<Report>('/reports', request);
  },

  // Start async report generation using AI (returns immediately with reportId)
  generateReport: async (request: GenerateReportRequest): Promise<{ reportId: string }> => {
    return await httpClient.post<{ reportId: string }>('/reports/generate', request);
  },

  // Poll for report generation progress (returns null when no progress available)
  getGenerationProgress: async (reportId: string): Promise<ReportGenerationProgress | null> => {
    try {
      const result = await httpClient.get<ReportGenerationProgress>(`/reports/generate/progress/${reportId}`);
      return result || null; // 204 returns undefined
    } catch {
      return null;
    }
  },

  // Get available case data for report generation
  getAvailableCaseData: async (caseId: string): Promise<CaseDataSummary> => {
    return await httpClient.get<CaseDataSummary>(`/reports/case/${caseId}/available-data`);
  },

  // Get reports by case ID
  getReportsByCase: async (caseId: string): Promise<Report[]> => {
    return await httpClient.get<Report[]>(`/reports/case/${caseId}`);
  },

  // Get all reports for current user
  getUserReports: async (): Promise<Report[]> => {
    return await httpClient.get<Report[]>('/reports/user');
  },

  // Get report by ID
  getReport: async (id: string): Promise<Report> => {
    return await httpClient.get<Report>(`/reports/${id}`);
  },

  // Update report
  updateReport: async (id: string, request: UpdateReportRequest): Promise<Report> => {
    return await httpClient.put<Report>(`/reports/${id}`, request);
  },

  // Update report status
  updateReportStatus: async (id: string, status: 'complete' | 'archived'): Promise<Report> => {
    // Using PUT instead of PATCH to avoid CORS caching issues
    return await httpClient.put<Report>(`/reports/${id}/status?status=${status}`);
  },

  // Revise full report using AI (non-streaming)
  reviseReport: async (id: string, request: ReviseReportRequest): Promise<RevisionResponse> => {
    return await httpClient.post<RevisionResponse>(`/reports/${id}/revise`, request, LONG_RUNNING_TIMEOUT);
  },

  // Revise a selection of text using AI (non-streaming)
  reviseSelection: async (id: string, request: ReviseSelectionRequest): Promise<RevisionResponse> => {
    return await httpClient.post<RevisionResponse>(`/reports/${id}/revise-selection`, request, LONG_RUNNING_TIMEOUT);
  },

  // Stream full report revision via SSE
  reviseReportStream: (
    id: string,
    request: ReviseReportRequest,
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: Error) => void
  ): Promise<void> => {
    return postSseStream(`/reports/${id}/revise-stream`, request, onToken, onDone, onError);
  },

  // Stream selection revision via SSE
  reviseSelectionStream: (
    id: string,
    request: ReviseSelectionRequest,
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: Error) => void
  ): Promise<void> => {
    return postSseStream(`/reports/${id}/revise-selection-stream`, request, onToken, onDone, onError);
  },

  // Stream content generation from documents via SSE
  generateContentStream: (
    id: string,
    request: GenerateContentRequest,
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: Error) => void
  ): Promise<void> => {
    return postSseStream(`/reports/${id}/generate-content-stream`, request, onToken, onDone, onError);
  },

  /**
   * Context-first section generation — single LLM call with pre-gathered case context.
   * Much faster than the agent loop (~5-10s vs 30-60s per section).
   */
  generateSectionStream: (
    reportId: string,
    request: { caseId: string; sectionTitle: string; sectionGuide?: string; otherSections?: string[] },
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: Error) => void
  ): Promise<void> => {
    return postSseStream(`/reports/${reportId}/generate-section-stream`, request, onToken, onDone, onError);
  },

  // Stream an "Ask" Q&A response via SSE (no report modification)
  askStream: (
    id: string,
    request: AskRequest,
    onToken: (token: string) => void,
    onDone: () => void,
    onError: (error: Error) => void,
    onContextLoaded?: (metadata: ContextLoadedEvent) => void,
  ): Promise<void> => {
    return postSseStream(`/reports/${id}/ask-stream`, request, onToken, onDone, onError, onContextLoaded);
  },

  // Export report as PDF, DOCX, or TXT (server-side generation)
  exportReport: async (id: string, format: 'pdf' | 'docx' | 'txt'): Promise<void> => {
    await httpClient.downloadFile(`/reports/${id}/export`, { format });
  },

  // Delete report
  deleteReport: async (id: string): Promise<void> => {
    await httpClient.delete(`/reports/${id}`);
  },

  // Parse ProseMirror content from JSON string
  parseContent: (contentJson: string): any => {
    try {
      return JSON.parse(contentJson);
    } catch (error) {
      console.error('Failed to parse report content:', error);
      return null;
    }
  },

  // Stringify ProseMirror content to JSON
  stringifyContent: (content: any): string => {
    return JSON.stringify(content);
  },

  // Version history
  getVersions: async (reportId: string): Promise<ReportVersionSummary[]> => {
    return await httpClient.get<ReportVersionSummary[]>(`/reports/${reportId}/versions`);
  },

  getVersion: async (reportId: string, versionId: string): Promise<ReportVersionFull> => {
    return await httpClient.get<ReportVersionFull>(`/reports/${reportId}/versions/${versionId}`);
  },

  createVersionSnapshot: async (reportId: string, changeType: string, changeDescription?: string): Promise<ReportVersionSummary> => {
    return await httpClient.post<ReportVersionSummary>(`/reports/${reportId}/versions`, {
      changeType,
      changeDescription,
    });
  },

  revertToVersion: async (reportId: string, versionId: string): Promise<ReportVersionSummary> => {
    return await httpClient.post<ReportVersionSummary>(`/reports/${reportId}/versions/${versionId}/revert`);
  },
};

// Extracted image types and API
export interface ExtractedImageSummary {
  id: string;
  filename: string;
  sourceDocumentId: string;
  pageNumber: number;
  imageIndex: number;
  width: number;
  height: number;
  contentType: string;
  aiAnalysis?: string;
}

export interface ReportVersionSummary {
  id: string;
  reportId: string;
  title: string;
  versionNumber: number;
  changeType: 'manual_edit' | 'ai_revision' | 'ai_selection_revision' | 'ai_generation' | 'periodic_snapshot' | 'revert';
  changeDescription?: string;
  userId: string;
  createdAt: string;
}

export interface ReportVersionFull extends ReportVersionSummary {
  content: string;
}

// ─── Agent Chat API ────────────────────────────────────────────────────────────

export interface AgentChatRequest {
  message: string;
  caseId: string;
  conversationId?: string | null;
}

export interface AgentToolCallEvent {
  toolName: string;
  toolUseId: string;
  input: Record<string, unknown>;
}

export interface AgentToolResultEvent {
  toolName: string;
  toolUseId: string;
  summary: string;
  widgetData?: ToolWidgetData;
}

export interface AgentDoneEvent {
  conversationId: string;
  messageId?: string;
  toolCallCount?: number;
}

export interface AgentSourceEvent {
  documentName: string;
  pageNumber?: number;
  excerpt?: string;
  documentId?: string;
  sourceText?: string;
  chunkIndex?: number;
}

export interface AgentChatCallbacks {
  onThinking: (message: string) => void;
  onToolCall: (event: AgentToolCallEvent) => void;
  onToolResult: (event: AgentToolResultEvent) => void;
  onToken: (token: string) => void;
  onSources?: (sources: AgentSourceEvent[]) => void;
  onDone: (event: AgentDoneEvent) => void;
  onError: (error: Error) => void;
}

async function consumeAgentSseStream(
  response: Response,
  callbacks: AgentChatCallbacks,
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    callbacks.onError(new Error('No response body'));
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = '';
  let dataLines: string[] = [];

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event:')) {
          eventName = line.slice(6).trim();
        } else if (line.startsWith('data:')) {
          dataLines.push(line.slice(5));
        } else if (line === '') {
          if (dataLines.length > 0) {
            const data = dataLines.join('\n');
            switch (eventName) {
              case 'thinking':
                try { callbacks.onThinking(JSON.parse(data).message || data); } catch { callbacks.onThinking(data); }
                break;
              case 'tool_call':
                try { callbacks.onToolCall(JSON.parse(data)); } catch { /* ignore */ }
                break;
              case 'tool_result':
                try { callbacks.onToolResult(JSON.parse(data)); } catch { /* ignore */ }
                break;
              case 'token':
                callbacks.onToken(data);
                break;
              case 'sources':
                try {
                  const parsed = JSON.parse(data);
                  if (parsed.sources && callbacks.onSources) callbacks.onSources(parsed.sources);
                } catch { /* ignore */ }
                break;
              case 'done':
                try { callbacks.onDone(JSON.parse(data)); } catch { callbacks.onDone({ conversationId: '' }); }
                return;
              case 'error':
                try {
                  const errObj = JSON.parse(data);
                  callbacks.onError(new Error(errObj.message || data));
                } catch {
                  callbacks.onError(new Error(data || 'Agent streaming failed'));
                }
                return;
            }
            dataLines = [];
          }
          eventName = '';
        }
      }
    }
    // Stream ended without done — still complete
    callbacks.onDone({ conversationId: '' });
  } catch (err) {
    callbacks.onError(err instanceof Error ? err : new Error(String(err)));
  }
}

/** Check if a case has indexed documents available for AI assistant search. */
export async function getCaseIndexingStatus(caseId: string): Promise<{ isIndexed: boolean; chunkedDocuments: number }> {
  return httpClient.get(`/processing/chunking/cases/${caseId}/status`);
}

export const agentApi = {
  chat: async (request: AgentChatRequest, callbacks: AgentChatCallbacks): Promise<void> => {
    const token = TokenManager.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    try {
      const response = await fetch(`${API_BASE_URL}/reports/agent/chat`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
        credentials: 'include',
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => response.statusText);
        callbacks.onError(new Error(`Agent request failed (${response.status}): ${errorText}`));
        return;
      }

      await consumeAgentSseStream(response, callbacks);
    } catch (err) {
      callbacks.onError(err instanceof Error ? err : new Error(String(err)));
    }
  },

  getConversations: async (caseId: string): Promise<AgentConversationSummary[]> => {
    return await httpClient.get<AgentConversationSummary[]>(`/reports/agent/conversations?caseId=${caseId}`);
  },

  deleteConversation: async (conversationId: string): Promise<void> => {
    await httpClient.delete(`/reports/agent/conversations/${conversationId}`);
  },

  getConversationMessages: async (conversationId: string): Promise<AgentMessageDTO[]> => {
    return await httpClient.get<AgentMessageDTO[]>(`/reports/agent/conversations/${conversationId}/messages`);
  },

  getTabLayout: async (caseId: string): Promise<AgentTabLayout | null> => {
    return await httpClient.get<AgentTabLayout | null>(`/reports/agent/tab-layout?caseId=${caseId}`);
  },

  saveTabLayout: async (caseId: string, layout: AgentTabLayout): Promise<void> => {
    await httpClient.put('/reports/agent/tab-layout', { caseId, layout });
  },
};

export interface AgentMessageDTO {
  id: string;
  role: string;
  content: string;
  toolCalls: string | null;
  createdAt: string;
}

export interface AgentTabLayout {
  tabOrder: string[];
  activeTabId: string | null;
}

export interface AgentConversationSummary {
  id: string;
  caseId: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
}

export const imageApi = {
  getCaseImages: async (caseId: string): Promise<ExtractedImageSummary[]> => {
    return await httpClient.get<ExtractedImageSummary[]>(`/extracted-images/case/${caseId}`);
  },

  getImageDownloadUrl: (imageId: string): string => {
    return `${API_BASE_URL}/extracted-images/${imageId}/download`;
  },
};
